import React from 'react';
import Modal from './Modal';
import Button from '../atoms/Button';
import { AlertTriangle } from 'lucide-react';

/**
 * ConfirmDialog Component - Feedback
 * Modern replacement for window.confirm()
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Dialog open state
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onConfirm - Confirm handler
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Dialog message
 * @param {string} props.confirmText - Confirm button text
 * @param {string} props.cancelText - Cancel button text
 * @param {string} props.variant - 'danger' | 'warning' | 'info'
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning',
  ...props
}) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const variants = {
    danger: {
      icon: 'text-error-500',
      bg: 'bg-error-50 dark:bg-error-900/20',
      confirmVariant: 'danger',
    },
    warning: {
      icon: 'text-warning-500',
      bg: 'bg-warning-50 dark:bg-warning-900/20',
      confirmVariant: 'accent',
    },
    info: {
      icon: 'text-info-500',
      bg: 'bg-info-50 dark:bg-info-900/20',
      confirmVariant: 'primary',
    },
  };

  const variantStyle = variants[variant] || variants.warning;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={null}
      {...props}
    >
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className={`p-3 rounded-full ${variantStyle.bg}`}>
            <AlertTriangle className={variantStyle.icon} size={32} />
          </div>
        </div>
        <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-2">
          {title}
        </h3>
        {message && (
          <p className="text-sm text-primary-600 dark:text-primary-400 mb-6 px-2">
            {message}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto sm:min-w-[120px]"
          >
            {cancelText}
          </Button>
          <Button
            variant={variantStyle.confirmVariant}
            onClick={handleConfirm}
            className="w-full sm:w-auto sm:min-w-[120px]"
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
